/**
 * SbarFormWidget — SBAR clinical communication form (DRR-321)
 *
 * SBAR = Situation, Background, Assessment, Recommendation
 * Standard healthcare communication format for handoffs and escalations.
 *
 * This widget provides a quick-entry SBAR form for clinical communication.
 * Submissions are tracked locally until messaging backend is integrated.
 */

import React, { useState } from 'react';
import {
  Stack,
  Text,
  Group,
  Card,
  Alert,
  Textarea,
  Button,
  Select,
  Accordion,
  ThemeIcon,
} from '@mantine/core';
import {
  IconMessageReport,
  IconSend,
  IconInfoCircle,
  IconClipboardList,
  IconBulb,
  IconChecklist,
} from '@tabler/icons-react';
import type { WidgetProps } from '@ferp/core';

interface SbarFormData {
  situation: string;
  background: string;
  assessment: string;
  recommendation: string;
  priority: string;
  recipientRole: string;
}

const PRIORITY_OPTIONS = [
  { value: 'routine', label: 'Routine' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'stat', label: 'STAT / Emergency' },
];

const RECIPIENT_OPTIONS = [
  { value: 'physician', label: 'Physician' },
  { value: 'nurse-supervisor', label: 'Nurse Supervisor' },
  { value: 'charge-nurse', label: 'Charge Nurse' },
  { value: 'care-team', label: 'Care Team' },
];

export function SbarFormWidget(_props: WidgetProps) {
  const [expanded, setExpanded] = useState(false);
  const [formData, setFormData] = useState<SbarFormData>({
    situation: '',
    background: '',
    assessment: '',
    recommendation: '',
    priority: 'routine',
    recipientRole: 'physician',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    // SBAR submission tracked locally until messaging backend integration
    setSubmitted(true);
    setFormData({
      situation: '',
      background: '',
      assessment: '',
      recommendation: '',
      priority: 'routine',
      recipientRole: 'physician',
    });
    setTimeout(() => setSubmitted(false), 3000);
  };

  const isValid = formData.situation.length > 10 && formData.assessment.length > 10;

  if (submitted) {
    return (
      <Alert icon={<IconSend size={16} />} color="green" variant="light">
        SBAR communication recorded.
      </Alert>
    );
  }

  if (!expanded) {
    return (
      <Card withBorder padding="sm" radius="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon size="sm" color="blue" variant="light">
              <IconMessageReport size={14} />
            </ThemeIcon>
            <Text fw={500} size="sm">SBAR Communication</Text>
          </Group>
          <Button
            size="xs"
            variant="light"
            onClick={() => setExpanded(true)}
          >
            New SBAR
          </Button>
        </Group>
        <Text size="xs" c="dimmed" mt="xs">
          Quick clinical handoff and escalation communication using the SBAR format.
        </Text>
      </Card>
    );
  }

  return (
    <Stack gap="sm">
      <Group gap="xs" justify="space-between">
        <Group gap="xs">
          <IconMessageReport size={16} color="var(--mantine-color-blue-6)" />
          <Text fw={600} size="sm">SBAR Communication</Text>
        </Group>
        <Button size="xs" variant="subtle" onClick={() => setExpanded(false)}>
          Cancel
        </Button>
      </Group>

      <Group grow>
        <Select
          size="xs"
          label="Priority"
          data={PRIORITY_OPTIONS}
          value={formData.priority}
          onChange={(v) => setFormData(prev => ({ ...prev, priority: v ?? 'routine' }))}
        />
        <Select
          size="xs"
          label="To"
          data={RECIPIENT_OPTIONS}
          value={formData.recipientRole}
          onChange={(v) => setFormData(prev => ({ ...prev, recipientRole: v ?? 'physician' }))}
        />
      </Group>

      <Accordion variant="separated" defaultValue="situation">
        <Accordion.Item value="situation">
          <Accordion.Control icon={<IconInfoCircle size={16} />}>
            <Text size="sm" fw={500}>Situation</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Textarea
              size="xs"
              placeholder="What is happening right now? Be concise and specific."
              value={formData.situation}
              onChange={(e) => setFormData(prev => ({ ...prev, situation: e.target.value }))}
              minRows={2}
            />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="background">
          <Accordion.Control icon={<IconClipboardList size={16} />}>
            <Text size="sm" fw={500}>Background</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Textarea
              size="xs"
              placeholder="Relevant history, diagnoses, treatments. What led to this situation?"
              value={formData.background}
              onChange={(e) => setFormData(prev => ({ ...prev, background: e.target.value }))}
              minRows={2}
            />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="assessment">
          <Accordion.Control icon={<IconBulb size={16} />}>
            <Text size="sm" fw={500}>Assessment</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Textarea
              size="xs"
              placeholder="What do you think is going on? Your clinical judgment."
              value={formData.assessment}
              onChange={(e) => setFormData(prev => ({ ...prev, assessment: e.target.value }))}
              minRows={2}
            />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="recommendation">
          <Accordion.Control icon={<IconChecklist size={16} />}>
            <Text size="sm" fw={500}>Recommendation</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Textarea
              size="xs"
              placeholder="What do you need? Orders, evaluation, transfer, etc."
              value={formData.recommendation}
              onChange={(e) => setFormData(prev => ({ ...prev, recommendation: e.target.value }))}
              minRows={2}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Button
        size="sm"
        leftSection={<IconSend size={14} />}
        onClick={handleSubmit}
        disabled={!isValid}
      >
        Send SBAR
      </Button>
    </Stack>
  );
}
